import { getAppConfigSingleton } from "@expert/app-config";
import { ExpertUniversityConfigurationProvider, partnerEnabledForExpertUniversity } from "@expert/expert-university";
import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { getLogger } from "@expert/logging";
import { useGuideStore } from "@expert/solve-tools";
import { Flex } from "@mantine/core";
import { useWindowEvent } from "@mantine/hooks";
import { AnalyticsProvider } from "@soluto-private/eventualize-react";
import { useTimelineStore } from "@soluto-private/expert-workspace-timeline";
import { type JSX, type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useAutopilotEventsListener } from "@/amazon-connect/chat/hooks/useAutopilotEventsListener";
import { useNeglectionEventListener } from "@/amazon-connect/chat/hooks/useNeglectionEventListener";
import { useCallDetailsStore } from "@/call-details";
import { useSetGaiaSessionContext } from "@/components/workspace/hooks/useSetGaiaSessionContext";
import { WorkspaceContent } from "@/components/workspace/WorkspaceContent";
import { env } from "@/config";
import { useVerifiedCustomerStore } from "@/customer-info";
import { useVerificationFlowStore } from "@/customer-panel";
import { useCallIntroStore } from "@/customer-panel/components/call-intro-flow/store/CallIntroStore";
import { useTechCuesListener } from "@/gaia-client/hooks/useTechCuesListener";
import { usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { useProductOfferStore } from "@/sales-tools";
import { useChallengeEventListener } from "@/sales-tools/challenges/hooks/useChallengeEventListener";
import { useDetectedCuesStore } from "@/sales-tools/detected-cues/stores/DetectedCuesStore";
import { useEligibilityPrecheck } from "@/sales-tools/home-product/hooks/useEligibilityPrecheck";
import { useEnrollmentEventListener } from "@/sales-tools/home-product/hooks/useEnrollEventListener";
import { useIntegratedFlowStore } from "@/sales-tools/home-product/stores/useIntegratedFlowStore";
import { useOfferChecklistStore } from "@/sales-tools/home-product/stores/useOfferChecklistStore";
import { useRapportBuildingListener } from "@/sales-tools/rapport-building/useRapportBuildingListener";
import { useSalesChecklistEventListener } from "@/sales-tools/sales-checklist/useSalesChecklistEventListener";
import {
    DevDebugTools,
    isSimulatedVoiceTask,
    isTrainingTask,
    sdkEventBus,
    useActiveTask,
    useAgentSdk,
    useAgentStore,
    useChatTasks,
    usePartner,
    useSession,
} from "@/sdk";
import { useSessionSummaryListener } from "@/session-summary/api/useSessionSummaryListener";
import { userCache } from "@/shared-utils";
import { useTroubleshootingStore } from "@/troubleshoot";
import { WorkspaceHeader } from "../header";
import { useInitExpertMonitoringTools } from "./hooks/useExpertMonitoringTools";
import { useWrapupToasts } from "./hooks/useWrapupToasts";
import styles from "./workspace.module.css";

const logger = getLogger({ module: "Workspace" });

export function Workspace({ isAuthRedirectNeededRef }: { isAuthRedirectNeededRef: RefObject<boolean> }): JSX.Element {
    const activeTask = useActiveTask();
    const navigate = useNavigate();
    const sdk = useAgentSdk();
    const { agentId, activity: currentActivity } = useAgentStore((state) => state);
    const { isTroubleshooting, setIsTroubleshooting } = useTroubleshootingStore();
    const chatTasks = useChatTasks();
    const session = useSession();
    const sessionId = session.id;

    const isCustomerSession = session.kind === "with-customer";
    const partner = usePartner();
    const activeTaskIdentities = activeTask ? { TaskId: activeTask.id } : {};
    const activeTaskExtra = activeTask
        ? {
              taskType: activeTask.type,
              partner: activeTask.partner,
          }
        : {};

    const {
        simplifiedSummary,
        callControlsEnabled,
        customerPanelEnabled,
        logOutOnOfflineAgentActivity,
        isRealtimeTranscriptEnabled,
    } = usePartnerFeatureConfigured();
    const {
        features: { isMicroLearningV2Enabled, logoutPendingDuration, isChallengeEnabled },
    } = useFeatures<WorkspaceFeatures>();
    const [pendingLogout, setPendingLogout] = useState(false);

    const [hasCallEnded, setHasCallEnded] = useState(false);

    const activeTrainingTask = useMemo(() => {
        return isTrainingTask(activeTask) ? activeTask : null;
    }, [activeTask]);

    const showExpertUniversity =
        !activeTask &&
        (currentActivity === "Training" || currentActivity === "Residency") &&
        partnerEnabledForExpertUniversity(partner, env.channel);

    const activeExpertUniversityTask = useMemo(() => {
        return isSimulatedVoiceTask(activeTask) ? activeTask : null;
    }, [activeTask]);
    const showRealtimeTranscript = Boolean(
        (isRealtimeTranscriptEnabled && isCustomerSession) ||
            (activeExpertUniversityTask && partnerEnabledForExpertUniversity(partner, env.channel)),
    );

    useTechCuesListener();
    useSetGaiaSessionContext();
    const shouldListenForSessionSummary = simplifiedSummary && env.channel !== "chat";
    useSessionSummaryListener(shouldListenForSessionSummary);
    useSalesChecklistEventListener();
    useRapportBuildingListener();
    useChallengeEventListener();
    useEnrollmentEventListener();
    useEligibilityPrecheck();
    useNeglectionEventListener();
    useAutopilotEventsListener();

    useEffect(() => {
        const logoutTimeout = setTimeout(() => {
            if (pendingLogout) {
                sdk.logout?.();
                localStorage.clear();
                sessionStorage.clear();
                void navigate("/logged-out");
            }
        }, logoutPendingDuration);

        return () => clearTimeout(logoutTimeout);
        // oxlint-disable-next-line exhaustive-deps
    }, [pendingLogout, sdk?.logout, logoutPendingDuration, navigate]);

    useEffect(() => {
        const agentOfflineEvent = sdkEventBus.on("agent_activity_changed", ({ previousActivity, activity }) => {
            // ignore event if activity is unchanged (e.g. updatedAt changed but activity didn't)
            if (activity === previousActivity) return;
            if (logOutOnOfflineAgentActivity && activity === sdk.offlineAgentActivity) {
                setPendingLogout(true);
            }
        });

        const technicalIssuesEvent = sdkEventBus.on("technical_issues", () => {
            logger.info("Troubleshooting | open");
            setIsTroubleshooting(true);
        });

        const sessionChangedEvent = sdkEventBus.on("session_changed", ({ action }) => {
            if (action === "end" && isMicroLearningV2Enabled) {
                logger.info("MicroLearning triggered");
                setHasCallEnded(true);
            }
        });
        return () => {
            agentOfflineEvent();
            technicalIssuesEvent();
            sessionChangedEvent();
        };
        // oxlint-disable-next-line exhaustive-deps
    }, [setIsTroubleshooting, isMicroLearningV2Enabled]);

    useWrapupToasts();

    useWindowEvent("beforeunload", (ev) => {
        // show confirmation dialog while with a customer (e.g. on a call) unless we're redirecting to re-authenticate
        if (!isCustomerSession || isAuthRedirectNeededRef.current) return;
        ev.preventDefault();
        ev.returnValue = "";
        return "";
    });

    useInitExpertMonitoringTools();

    useEffect(() => {
        // Check if partner config exists in the appConfigSingleton on partner change and warn once if not
        if (partner && partner !== "unknown") {
            const appConfig = getAppConfigSingleton();
            if (!appConfig.hasPartnerConfig(partner)) {
                logger.warn(`Partner configuration not found for '${partner}', 'unknown' configuration will be used.`);
            }
        }
    }, [partner]);

    useEffect(() => {
        if (env.channel === "chat") {
            useTimelineStore.getState().setUseDefaultTimelineKey(false);
        }
    }, []);

    useEffect(() => {
        // rehydrate stores if current session is with a customer and it has not ended
        if (session.metadata.status !== "ended" && session.kind === "with-customer") {
            void useVerificationFlowStore.persist.rehydrate();
            void useCallDetailsStore.persist.rehydrate();
            void useProductOfferStore.persist.rehydrate();
            void useVerifiedCustomerStore.persist.rehydrate();
            void useGuideStore.persist.rehydrate();
            void useOfferChecklistStore.persist.rehydrate();
            void useDetectedCuesStore.persist.rehydrate();
            void useIntegratedFlowStore.persist.rehydrate();
            void useCallIntroStore.persist.rehydrate();
        }
    }, [session]);

    const containerRef = useRef(null);

    return (
        <Flex ref={containerRef} className={styles.container}>
            {env.isProdMode ? null : <DevDebugTools />}
            <ExpertUniversityConfigurationProvider userCache={userCache} channel={env.channel}>
                <AnalyticsProvider
                    dispatcher={(dispatcher) =>
                        dispatcher
                            .withIdentities({
                                WorkerId: agentId,
                                SessionId: sessionId,
                                ...activeTaskIdentities,
                            })
                            .withExtra({ partner, ...activeTaskExtra })
                    }
                >
                    <WorkspaceHeader />

                    <WorkspaceContent
                        activeExpertUniversityTask={activeExpertUniversityTask}
                        activeTrainingTask={activeTrainingTask}
                        callControlsEnabled={callControlsEnabled}
                        challengeEnabled={isChallengeEnabled}
                        chatTasks={chatTasks}
                        currentActivity={currentActivity}
                        customerPanelEnabled={customerPanelEnabled}
                        customerSession={isCustomerSession}
                        hasCallEnded={hasCallEnded}
                        microLearningV2Enabled={isMicroLearningV2Enabled}
                        showRealtimeTranscript={showRealtimeTranscript}
                        sdk={sdk}
                        session={session}
                        showExpertUniversity={showExpertUniversity}
                        troubleshooting={isTroubleshooting}
                    />
                </AnalyticsProvider>
            </ExpertUniversityConfigurationProvider>
        </Flex>
    );
}
